import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { StoreButton } from "./StoreButton";
import { Phone } from "./Phone";
import { CalendarDays } from "lucide-react";

const lines = ["Catch.", "Compete.", "Climb."];
const ease = [0.22, 1, 0.36, 1];

const Line = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${i === 2 ? "text-outline" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-28 sm:pt-32 pb-16">
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" />
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#00E5FF]/10 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-10 px-5 sm:px-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em] sm:tracking-[0.3em] text-[#00E5FF]">
            <span className="h-2 w-2 rounded-full bg-[#F59E0B] pulse-dot" /> Now in beta · Full release spring 2027
          </motion.p>
          <h1 data-testid="hero-title"
            className="font-display text-[22vw] sm:text-[16vw] lg:text-[9rem] xl:text-[10.5rem] font-black uppercase leading-[0.86] tracking-tight text-white">
            {lines.map((l, i) => <Line key={l} text={l} i={i} />)}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease }}>
            <p className="mt-8 max-w-lg text-base md:text-lg text-slate-300 leading-relaxed">
              Angler League turns every cast into competition. Join leagues, log your catches and climb the board against anglers who fish like you do.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <StoreButton id="hero" />
              <div data-testid="hero-release-date"
                className="flex items-center gap-3 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 py-2.5 pl-4 pr-5">
                <CalendarDays className="h-5 w-5 text-[#F59E0B]" />
                <span className="flex flex-col leading-none">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#F59E0B]">Full release</span>
                  <span className="font-display text-xl font-extrabold uppercase text-white">March 2027</span>
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative [perspective:1200px]">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
            transition={{ duration: 1.4, delay: 0.35, ease }}
            style={{ rotateX: rx, rotateY: ry }}
            className="relative aspect-[4/5] w-full max-w-md mx-auto overflow-hidden rounded-[28px] border border-cyan-400/20 [transform-style:preserve-3d]"
          >
            <motion.img src="/img/hero.jpg" alt="Angler casting on open water" style={{ y: imgY, scale: 1.2 }}
              className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050911] via-transparent to-[#050911]/30" />
            <div className="absolute inset-0 bg-[#050911]/40" />
            <div className="absolute inset-0 spotlight" />
            <div className="absolute inset-0 radar-sweep" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.9, ease }}
            style={{ rotateX: rx, rotateY: ry, z: 60 }}
            className="absolute inset-x-0 top-[6%] mx-auto w-[56%] max-w-[250px]"
          >
            <Phone src="/img/post.png" alt="Angler League post a catch screen" testid="hero-phone" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
